import React from "react";
import { useResource } from "../hooks";
import { api, fmtNum, fmtINR } from "../api";
import AskOIM from "../components/AskOIM";

function CarbonGauge({ value, max = 4.0, benchmark }) {
  const pct = Math.min(1, value / max);
  const angle = -135 + pct * 270;
  const r = 90;
  const cx = 110, cy = 110;
  const arc = (start, end, color) => {
    const a1 = (start * Math.PI) / 180;
    const a2 = (end * Math.PI) / 180;
    const x1 = cx + r * Math.cos(a1), y1 = cy + r * Math.sin(a1);
    const x2 = cx + r * Math.cos(a2), y2 = cy + r * Math.sin(a2);
    const large = end - start > 180 ? 1 : 0;
    return <path d={`M ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2}`} stroke={color} strokeWidth="10" fill="none" strokeLinecap="square" />;
  };
  // bench position marker
  const benchPct = Math.min(1, benchmark / max);
  const benchA = (-135 + benchPct * 270) * Math.PI / 180;
  const bx1 = cx + (r - 8) * Math.cos(benchA);
  const by1 = cy + (r - 8) * Math.sin(benchA);
  const bx2 = cx + (r + 8) * Math.cos(benchA);
  const by2 = cy + (r + 8) * Math.sin(benchA);

  return (
    <div className="gauge">
      <svg viewBox="0 0 220 220" width="100%" height="100%">
        {/* track */}
        {arc(-135, 135, "var(--border-strong)")}
        {/* zones */}
        {arc(-135, -45, "var(--status-healthy)")}
        {arc(-45, 45, "var(--status-warning)")}
        {arc(45, 135, "var(--status-critical)")}
        {/* needle */}
        <line
          x1={cx} y1={cy}
          x2={cx + (r - 14) * Math.cos(angle * Math.PI / 180)}
          y2={cy + (r - 14) * Math.sin(angle * Math.PI / 180)}
          stroke="#fff" strokeWidth="2"
        />
        <circle cx={cx} cy={cy} r="6" fill="var(--brand-amber)" />
        {/* benchmark tick */}
        <line x1={bx1} y1={by1} x2={bx2} y2={by2} stroke="var(--text-primary)" strokeWidth="2" />
      </svg>
      <div className="gauge-value">
        <div className="v">{fmtNum(value, 2)}</div>
        <div className="l">kg CO₂ / kg</div>
      </div>
    </div>
  );
}

function PieScope({ scope1, scope2, process }) {
  const total = scope1 + scope2 + process || 1;
  const segs = [
    { v: scope1 / total, label: "Scope 1 (Boilers)", color: "var(--brand-amber)" },
    { v: scope2 / total, label: "Scope 2 (Electricity)", color: "var(--brand-green)" },
    { v: process / total, label: "Process / Embodied", color: "var(--brand-navy-soft)" },
  ];
  let cum = 0;
  const cx = 100, cy = 100, r = 70;
  const arcs = segs.map((s, i) => {
    const a1 = cum * 2 * Math.PI - Math.PI / 2;
    cum += s.v;
    const a2 = cum * 2 * Math.PI - Math.PI / 2;
    const x1 = cx + r * Math.cos(a1), y1 = cy + r * Math.sin(a1);
    const x2 = cx + r * Math.cos(a2), y2 = cy + r * Math.sin(a2);
    const large = s.v > 0.5 ? 1 : 0;
    return <path key={i} d={`M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`} fill={s.color} stroke="var(--bg-surface)" strokeWidth="2" />;
  });
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
      <svg viewBox="0 0 200 200" width="180" height="180">{arcs}</svg>
      <div className="stack" style={{ gap: 8 }}>
        {segs.map((s, i) => (
          <div key={i} className="flex" style={{ gap: 10 }}>
            <span style={{ width: 10, height: 10, background: s.color, display: "inline-block" }} />
            <span className="mono" style={{ fontSize: 12, color: "var(--text-secondary)" }}>{s.label}</span>
            <span className="mono" style={{ fontSize: 12, color: "var(--text-primary)" }}>{Math.round(s.v * 100)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function SustainabilityCenter() {
  const data = useResource(() => api.sustain(), [], 3000);
  if (!data) return <div className="muted">Loading sustainability data…</div>;
  const c = data.carbon || {};

  return (
    <div className="stack" data-testid="sustainability-center">
      <div className="page-title">
        <div>
          <h1>Sustainability Center</h1>
          <div className="sub">Scope 1 · Scope 2 · Product Carbon Footprint</div>
        </div>
      </div>

      <div className="grid-3">
        <RibbonCell label="Scope 1 (Daily)" value={fmtNum(c.scope1_daily_t, 1)} unit="tCO₂" />
        <RibbonCell label="Scope 2 (Daily)" value={fmtNum(c.scope2_daily_t, 1)} unit="tCO₂" />
        <RibbonCell label="PCF" value={fmtNum(c.pcf, 2)} unit="kg/kg aniline" amber />
      </div>

      <div className="grid-2">
        <div className="card" data-testid="carbon-gauge">
          <div className="card-header"><div className="card-title">Carbon Intensity Gauge</div><span className="chip">Live</span></div>
          <div className="card-body" style={{ textAlign: "center" }}>
            <CarbonGauge value={c.pcf || 0} max={4.0} benchmark={2.45} />
            <div className="mono" style={{ fontSize: 11, color: "var(--text-tertiary)", letterSpacing: "0.06em", marginTop: 10 }}>
              Benchmark <strong style={{ color: "var(--text-primary)" }}>2.45</strong> · max display <strong>4.0</strong> kg CO₂ / kg
            </div>
            <AskOIM question="Why has PCF deviated from the benchmark?" source="sustainability" />
          </div>
        </div>

        <div className="card" data-testid="scope-split">
          <div className="card-header"><div className="card-title">Scope Split</div></div>
          <div className="card-body" style={{ display: "flex", justifyContent: "center" }}>
            <PieScope scope1={c.scope1_kgh || 0} scope2={c.scope2_kgh || 0} process={c.process_emissions_kgh || 0} />
          </div>
        </div>
      </div>

      <div className="card" data-testid="pcf-table">
        <div className="card-header"><div className="card-title">Product Carbon Footprint</div><span className="chip">kg CO₂ per kg product</span></div>
        <div style={{ padding: 0 }}>
          <table className="tbl">
            <thead><tr><th>Product</th><th>Throughput</th><th>PCF (Current)</th><th>Benchmark</th><th>Raw Mat.</th><th>Steam</th><th>Electricity</th><th>Process</th></tr></thead>
            <tbody>
              {(data.products || []).map(p => (
                <tr key={p.product} data-testid={`pcf-row-${p.product}`}>
                  <td><strong>{p.product}</strong></td>
                  <td className="mono">{p.production_tph} t/h</td>
                  <td className="mono" style={{ color: p.pcf > p.benchmark ? "var(--brand-amber-soft)" : "var(--brand-green-soft)" }}>{fmtNum(p.pcf, 3)}</td>
                  <td className="mono muted">{fmtNum(p.benchmark, 2)}</td>
                  <td className="mono">{fmtNum(p.breakdown.raw_materials, 3)}</td>
                  <td className="mono">{fmtNum(p.breakdown.steam, 3)}</td>
                  <td className="mono">{fmtNum(p.breakdown.electricity, 3)}</td>
                  <td className="mono">{fmtNum(p.breakdown.process, 3)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function RibbonCell({ label, value, unit, amber }) {
  return (
    <div className="card">
      <div className="card-body">
        <div className="mono muted" style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase" }}>{label}</div>
        <div className="mono" style={{ fontSize: 32, fontWeight: 700, color: amber ? "var(--brand-amber-soft)" : "var(--text-primary)", marginTop: 4 }}>
          {value}<span style={{ fontSize: 12, marginLeft: 6, color: "var(--text-tertiary)" }}>{unit}</span>
        </div>
      </div>
    </div>
  );
}
